import React, { useState, useEffect } from 'react'
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { toast } from 'react-hot-toast'
import { usePopup } from '@/contexts/PopupContext'

interface OrderFormPopupProps {
  isOpen: boolean
  onClose: () => void
}

interface Customer {
  id: string
  name: string
  phone: string
}

interface Product {
  id: string
  name: string
  price: number
}

// Mock data - sẽ được thay thế bằng dữ liệu thật sau
const MOCK_CUSTOMERS: Customer[] = [
  { id: '1', name: 'Nguyễn Văn A', phone: '0901234567' },
  { id: '2', name: 'Trần Thị B', phone: '0901234568' },
  { id: '3', name: 'Lê Văn C', phone: '0901234569' },
]

const MOCK_PRODUCTS: Product[] = [
  { id: '1', name: 'Sản phẩm 1', price: 100000 },
  { id: '2', name: 'Sản phẩm 2', price: 200000 },
  { id: '3', name: 'Sản phẩm 3', price: 300000 },
]

interface OrderItem {
  product: Product
  quantity: number
}

export const OrderFormPopup: React.FC<OrderFormPopupProps> = ({ isOpen, onClose }) => {
  const { openPopup, closePopup } = usePopup()
  const prevIsOpen = React.useRef(isOpen)
  const [customerSearch, setCustomerSearch] = useState('')
  const [productSearch, setProductSearch] = useState('')
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [orderItems, setOrderItems] = useState<OrderItem[]>([])
  const [isCustomerSearchFocused, setIsCustomerSearchFocused] = useState(false)
  const [isProductSearchFocused, setIsProductSearchFocused] = useState(false)

  useEffect(() => {
    if (!prevIsOpen.current && isOpen) {
      openPopup()
    }
    if (prevIsOpen.current && !isOpen) {
      closePopup()
    }
    prevIsOpen.current = isOpen
  }, [isOpen])

  // Reset form khi đóng popup
  useEffect(() => {
    if (!isOpen) {
      setCustomerSearch('')
      setProductSearch('')
      setSelectedCustomer(null)
      setOrderItems([])
      setIsCustomerSearchFocused(false)
      setIsProductSearchFocused(false)
    }
  }, [isOpen])

  // Hàm reset form
  const resetForm = () => {
    setCustomerSearch('')
    setProductSearch('')
    setSelectedCustomer(null)
    setOrderItems([])
    setIsCustomerSearchFocused(false)
    setIsProductSearchFocused(false)
  }

  const filteredCustomers = MOCK_CUSTOMERS.filter(customer => 
    customerSearch
      ? customer.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
        customer.phone.includes(customerSearch)
      : true // Trả về tất cả nếu chưa search
  ).slice(0, 3) // Chỉ lấy 3 kết quả

  const filteredProducts = MOCK_PRODUCTS.filter(product =>
    productSearch
      ? product.name.toLowerCase().includes(productSearch.toLowerCase())
      : true // Trả về tất cả nếu chưa search
  ).slice(0, 3) // Chỉ lấy 3 kết quả

  const handleAddProduct = (product: Product) => {
    const existingItem = orderItems.find(item => item.product.id === product.id)
    if (existingItem) {
      setOrderItems(orderItems.map(item =>
        item.product.id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ))
    } else {
      setOrderItems([...orderItems, { product, quantity: 1 }])
    }
    setProductSearch('')
  }

  const handleUpdateQuantity = (productId: string, newQuantity: number) => {
    if (newQuantity === 0) {
      setOrderItems(orderItems.filter(item => item.product.id !== productId))
    } else {
      setOrderItems(orderItems.map(item =>
        item.product.id === productId
          ? { ...item, quantity: newQuantity }
          : item
      ))
    }
  }

  const totalAmount = orderItems.reduce(
    (sum, item) => sum + item.product.price * item.quantity,
    0
  )

  const handleSubmit = () => {
    if (!selectedCustomer) {
      toast.error('Vui lòng chọn khách hàng')
      return
    }
    if (orderItems.length === 0) {
      toast.error('Vui lòng thêm sản phẩm vào đơn hàng')
      return
    }

    // TODO: Xử lý lưu đơn hàng
    console.log('Order data:', {
      customer: selectedCustomer,
      items: orderItems,
      totalAmount
    })
    
    toast.success('Đã tạo đơn hàng thành công!')
    resetForm() // Reset form sau khi tạo đơn thành công
    onClose()
  }

  // Handle close với reset
  const handleClose = () => {
    resetForm()
    onClose()
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50"
        onClick={handleClose}
      />
      
      {/* Popup */}
      <div className="fixed p-2 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:w-[420px] w-[90%] max-h-[80vh] bg-white rounded-2xl shadow-2xl z-50">
        {/* Header */}
        <div className="p-4 border-b border-gray-100">
          <div className="pr-8">
            <h2 className="text-lg font-semibold text-gray-900">
              Tạo đơn hàng mới
            </h2>
            {selectedCustomer && (
              <p className="text-md text-gray-700 mt-2 line-clamp-1">
                Khách: {selectedCustomer.name} - {selectedCustomer.phone}
              </p>
            )}
          </div>
          
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 p-2 rounded-xl hover:bg-gray-100 active:bg-gray-200 transition-colors duration-200"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Chọn khách hàng */}
          {!selectedCustomer && (
            <div className="space-y-2">
              <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={customerSearch}
                  onChange={e => setCustomerSearch(e.target.value)}
                  onFocus={() => setIsCustomerSearchFocused(true)}
                  onBlur={() => {
                    setTimeout(() => setIsCustomerSearchFocused(false), 200)
                  }}
                  placeholder="Tìm khách hàng theo tên hoặc SĐT"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {isCustomerSearchFocused && (
                <div className="bg-gray-50 rounded-xl divide-y divide-gray-200">
                  {filteredCustomers.map(customer => (
                    <button
                      key={customer.id}
                      onClick={() => setSelectedCustomer(customer)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-100 transition-colors duration-150"
                    >
                      <div className="font-medium text-gray-900">{customer.name}</div>
                      <div className="text-sm text-gray-500">{customer.phone}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Thêm sản phẩm */}
          {selectedCustomer && (
            <div className="space-y-4">
              <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  onFocus={() => setIsProductSearchFocused(true)}
                  onBlur={() => {
                    setTimeout(() => setIsProductSearchFocused(false), 200)
                  }}
                  placeholder="Tìm sản phẩm theo tên"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {isProductSearchFocused && (
                <div className="bg-gray-50 rounded-xl divide-y divide-gray-200">
                  {filteredProducts.map(product => (
                    <button
                      key={product.id}
                      onClick={() => handleAddProduct(product)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-100 transition-colors duration-150"
                    >
                      <div className="font-medium text-gray-900">{product.name}</div>
                      <div className="text-sm text-gray-500">
                        {product.price.toLocaleString('vi-VN')}đ
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Danh sách sản phẩm đã chọn */}
              {orderItems.length > 0 && (
                <div className="space-y-2">
                  <div className="font-medium text-gray-900">Sản phẩm đã chọn</div>
                  <div className="bg-gray-50 rounded-xl divide-y divide-gray-200">
                    {orderItems.map(item => (
                      <div key={item.product.id} className="px-4 py-3 flex items-center justify-between">
                        <div>
                          <div className="text-gray-900 line-clamp-1">{item.product.name}</div>
                          <div className="text-sm text-gray-500 line-clamp-1">
                            {item.product.price.toLocaleString('vi-VN')}đ
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleUpdateQuantity(item.product.id, item.quantity - 1)}
                            className="w-8 h-8 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 active:bg-gray-300"
                          >
                            -
                          </button>
                          <span className="w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => handleUpdateQuantity(item.product.id, item.quantity + 1)}
                            className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 active:bg-blue-300"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="text-right font-medium pt-2 pr-4 text-blue-600">
                    Tổng tiền: {totalAmount.toLocaleString('vi-VN')}đ
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100">
          {selectedCustomer ? (
            <button
              onClick={handleSubmit}
              disabled={orderItems.length === 0}
              className={`
                w-full py-2.5 px-4 rounded-xl font-medium
                transition-colors duration-200
                ${orderItems.length > 0
                  ? 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }
              `}
            >
              Tạo đơn hàng
            </button>
          ) : (
            <button
              onClick={() => setCustomerSearch('')}
              className="w-full py-2.5 px-4 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300"
            >
              Chọn một khách hàng
            </button>
          )}
        </div>
      </div>
    </>
  )
} 